import { defaultIdFactory, type IdFactory } from "./ids";
import { cleanupLayout, equalSizes, normalizeSizes } from "./normalize";
import {
  findActiveGroup,
  findGroupContaining,
  mapTree,
} from "./traverse";
import {
  EMPTY_PANEL_ID,
  LAYOUT_SCHEMA_VERSION,
  type AddTarget,
  type Edge,
  type Layout,
  type LayoutNode,
  type PanelInstance,
  type PanelRef,
  type Split,
  type SplitDirection,
  type TabGroup,
} from "./types";

interface MutationOptions {
  /** Override la generation d'IDs (utilise pour les tests). */
  idFactory?: IdFactory;
}

/** Options enrichies pour `addPanel`. */
export interface AddPanelOptions extends MutationOptions {
  /** Vue initiale (sinon defaultViewId du registry au render). */
  viewId?: string;
  /** Params initiaux (data scope). */
  params?: Record<string, unknown>;
  /** Override du titre. */
  title?: string;
}

function resolveIdFactory(opts?: MutationOptions): IdFactory {
  return opts?.idFactory ?? defaultIdFactory;
}

/**
 * Construit une PanelInstance en omettant les champs optionnels undefined
 * (pour que la serialisation ne contienne pas `viewId: undefined` etc.).
 */
function buildInstance(
  id: string,
  panelId: PanelRef,
  opts: { viewId?: string; params?: Record<string, unknown>; title?: string }
): PanelInstance {
  const inst: { id: string; panelId: PanelRef; viewId?: string; params?: Record<string, unknown>; title?: string } = {
    id,
    panelId,
  };
  if (opts.viewId !== undefined) inst.viewId = opts.viewId;
  if (opts.params !== undefined) inst.params = opts.params;
  if (opts.title !== undefined) inst.title = opts.title;
  return inst;
}

/* ============================================================ */
/* Constructors                                                  */
/* ============================================================ */

/** Layout vide avec une TabGroup racine sans panels. */
export function emptyLayout(opts?: MutationOptions): Layout {
  const id = resolveIdFactory(opts)();
  return {
    version: LAYOUT_SCHEMA_VERSION,
    root: { kind: "tabs", id, panels: [], activeIndex: -1 },
  };
}

/** Layout avec un seul panel a la racine. */
export function singlePanelLayout(
  panelId: PanelRef,
  opts?: MutationOptions
): Layout {
  const idFactory = resolveIdFactory(opts);
  const groupId = idFactory();
  const instanceId = idFactory();
  return {
    version: LAYOUT_SCHEMA_VERSION,
    root: {
      kind: "tabs",
      id: groupId,
      panels: [{ id: instanceId, panelId }],
      activeIndex: 0,
    },
  };
}

/* ============================================================ */
/* addPanel                                                      */
/* ============================================================ */

export interface AddPanelResult {
  layout: Layout;
  instanceId: string;
}

export function addPanel(
  layout: Layout,
  panelId: PanelRef,
  target: AddTarget = { kind: "active-group" },
  opts?: AddPanelOptions
): AddPanelResult {
  const idFactory = resolveIdFactory(opts);
  const newInstance = buildInstance(idFactory(), panelId, {
    viewId: opts?.viewId,
    params: opts?.params,
    title: opts?.title,
  });

  if (target.kind === "active-group") {
    const active = findActiveGroup(layout);
    if (active) {
      return addPanelToGroup(layout, active.id, newInstance);
    }
    // Pas de group actif : layout vide. Inject dans la racine si TabGroup, sinon erreur.
    if (layout.root.kind === "tabs") {
      const newRoot: TabGroup = {
        ...layout.root,
        panels: [newInstance],
        activeIndex: 0,
      };
      return {
        layout: { ...layout, root: newRoot },
        instanceId: newInstance.id,
      };
    }
    throw new Error(
      "addPanel: layout has no active group and root is a Split — use kind:'group' or 'split-edge' instead"
    );
  }

  if (target.kind === "group") {
    return addPanelToGroup(layout, target.groupId, newInstance);
  }

  // split-edge : cree un nouveau group sibling du target via Split
  const newGroup: TabGroup = {
    kind: "tabs",
    id: idFactory(),
    panels: [newInstance],
    activeIndex: 0,
  };
  return {
    layout: createEdgeSplit(
      layout,
      target.targetNodeId,
      target.edge,
      newGroup,
      idFactory,
      target.sizes
    ),
    instanceId: newInstance.id,
  };
}

function addPanelToGroup(
  layout: Layout,
  groupId: string,
  instance: PanelInstance
): AddPanelResult {
  const newRoot = mapTree(layout.root, (n) => {
    if (n.kind === "tabs" && n.id === groupId) {
      // Quand on ajoute un VRAI panel a un group qui contient des
      // placeholders "empty" (cellule "Ajouter un panneau"), on retire les
      // placeholders au lieu d'empiler un onglet a cote : l'onglet vide se
      // "ferme" et est remplace par le panel choisi. On ne touche pas aux
      // placeholders quand on ajoute un autre placeholder (cas degenere).
      const base =
        instance.panelId !== EMPTY_PANEL_ID
          ? n.panels.filter((p) => p.panelId !== EMPTY_PANEL_ID)
          : n.panels;
      const newPanels = [...base, instance];
      return {
        ...n,
        panels: newPanels,
        activeIndex: newPanels.length - 1, // nouveau tab devient actif
      };
    }
    return n;
  });
  return {
    layout: { ...layout, root: newRoot },
    instanceId: instance.id,
  };
}

/* ============================================================ */
/* appendRootColumn — pattern TweetDeck                          */
/* ============================================================ */

/**
 * Ajoute un panel comme nouvelle colonne au niveau racine.
 * Pattern TweetDeck : un seul bouton "+", chaque clic ajoute une colonne
 * a droite, l'utilisateur la deplace/redimensionne ensuite.
 *
 * Cas geres :
 *   - root = TabGroup vide → reuse, le panel devient le seul tab
 *   - root = TabGroup non-vide → wrap dans Split [root, newGroup], direction row
 *   - root = Split direction row → push newGroup comme dernier enfant, sizes egalisees
 *   - root = Split direction column → wrap dans outer Split [root, newGroup], direction row
 */
export function appendRootColumn(
  layout: Layout,
  panelId: PanelRef,
  opts?: AddPanelOptions
): AddPanelResult {
  const idFactory = resolveIdFactory(opts);
  const newInstance = buildInstance(idFactory(), panelId, {
    viewId: opts?.viewId,
    params: opts?.params,
    title: opts?.title,
  });
  const newGroup: TabGroup = {
    kind: "tabs",
    id: idFactory(),
    panels: [newInstance],
    activeIndex: 0,
  };

  // Cas 1 : root = TabGroup vide → reuse
  if (layout.root.kind === "tabs" && layout.root.panels.length === 0) {
    const newRoot: TabGroup = {
      ...layout.root,
      panels: [newInstance],
      activeIndex: 0,
    };
    return {
      layout: { ...layout, root: newRoot },
      instanceId: newInstance.id,
    };
  }

  // Cas 4 : root = Split row → append child
  if (layout.root.kind === "split" && layout.root.direction === "row") {
    const newChildren = [...layout.root.children, newGroup];
    const newRoot: Split = {
      ...layout.root,
      children: newChildren,
      sizes: equalSizes(newChildren.length),
    };
    return {
      layout: { ...layout, root: newRoot },
      instanceId: newInstance.id,
    };
  }

  // Cas 2 + 3 : root = TabGroup non-vide OU Split column → wrap dans outer row Split
  const wrapper: Split = {
    kind: "split",
    id: idFactory(),
    direction: "row",
    children: [layout.root, newGroup],
    sizes: equalSizes(2),
  };
  return {
    layout: { ...layout, root: wrapper },
    instanceId: newInstance.id,
  };
}

/**
 * Mirror de `appendRootColumn` mais pour ajouter une LIGNE (panel en bas).
 *
 * Cas geres :
 *   - root = TabGroup vide → reuse, le panel devient le seul tab
 *   - root = TabGroup non-vide → wrap dans Split [root, newGroup], direction column
 *   - root = Split direction column → push newGroup comme dernier enfant, sizes egalisees
 *   - root = Split direction row → wrap dans outer Split [root, newGroup], direction column
 */
export function appendRootRow(
  layout: Layout,
  panelId: PanelRef,
  opts?: AddPanelOptions
): AddPanelResult {
  const idFactory = resolveIdFactory(opts);
  const newInstance = buildInstance(idFactory(), panelId, {
    viewId: opts?.viewId,
    params: opts?.params,
    title: opts?.title,
  });
  const newGroup: TabGroup = {
    kind: "tabs",
    id: idFactory(),
    panels: [newInstance],
    activeIndex: 0,
  };

  // Cas 1 : root = TabGroup vide → reuse
  if (layout.root.kind === "tabs" && layout.root.panels.length === 0) {
    const newRoot: TabGroup = {
      ...layout.root,
      panels: [newInstance],
      activeIndex: 0,
    };
    return {
      layout: { ...layout, root: newRoot },
      instanceId: newInstance.id,
    };
  }

  // Cas 4 : root = Split column → append child
  if (layout.root.kind === "split" && layout.root.direction === "column") {
    const newChildren = [...layout.root.children, newGroup];
    const newRoot: Split = {
      ...layout.root,
      children: newChildren,
      sizes: equalSizes(newChildren.length),
    };
    return {
      layout: { ...layout, root: newRoot },
      instanceId: newInstance.id,
    };
  }

  // Cas 2 + 3 : root = TabGroup non-vide OU Split row → wrap dans outer column Split
  const wrapper: Split = {
    kind: "split",
    id: idFactory(),
    direction: "column",
    children: [layout.root, newGroup],
    sizes: equalSizes(2),
  };
  return {
    layout: { ...layout, root: wrapper },
    instanceId: newInstance.id,
  };
}

/* ============================================================ */
/* removePanel                                                   */
/* ============================================================ */

/** Onglet « fan » : la vue d'un fan, ou une de ses sous-vues détachées
 *  (fan-chat / fan-info / fan-stats / fan-media). */
function isFanPanel(panelId: PanelRef): boolean {
  return panelId === "fan" || String(panelId).startsWith("fan-");
}

export function removePanel(layout: Layout, instanceId: string): Layout {
  // « Conversations se ferme en dernier » : si on demande à fermer le panneau
  // Conversations alors que des onglets fan de SON groupe sont encore ouverts,
  // on ferme le fan le plus RÉCENT à la place. Mitrailler la croix ferme donc
  // les fans un par un sans virer la liste par accident (cas mobile : la croix
  // ferme l'onglet ACTIF — si on était sur Conversations, c'est elle qui
  // partait). La liste ne se ferme que quand elle est seule dans son groupe.
  const group = findGroupContaining(layout, instanceId);
  if (group) {
    const target = group.panels.find((p) => p.id === instanceId);
    if (target?.panelId === "conversations") {
      const fans = group.panels.filter(
        (p) => p.id !== instanceId && isFanPanel(p.panelId),
      );
      const lastFan = fans[fans.length - 1];
      if (lastFan) {
        return removePanel(layout, lastFan.id);
      }
    }
  }

  let touched = false;
  const newRoot = mapTree(layout.root, (n) => {
    if (n.kind !== "tabs") return n;
    const idx = n.panels.findIndex((p) => p.id === instanceId);
    if (idx < 0) return n;
    touched = true;
    const newPanels = n.panels.filter((_, i) => i !== idx);
    let newActive = n.activeIndex;
    if (newPanels.length === 0) newActive = -1;
    else if (idx === n.activeIndex) newActive = Math.max(0, idx - 1);
    else if (idx < n.activeIndex) newActive = n.activeIndex - 1;
    return { ...n, panels: newPanels, activeIndex: newActive };
  });
  if (!touched) return layout;
  return cleanupLayout({ ...layout, root: newRoot });
}

/* ============================================================ */
/* moveTab                                                       */
/* ============================================================ */

/**
 * Deplace un tab :
 *   - meme group, autre index → reorder
 *   - autre group → cross-group move (plus cleanup auto si source vide)
 */
export function moveTab(
  layout: Layout,
  sourceInstanceId: string,
  targetGroupId: string,
  targetIndex: number
): Layout {
  const sourceGroup = findGroupContaining(layout, sourceInstanceId);
  if (!sourceGroup) return layout;
  const sourceInstance = sourceGroup.panels.find(
    (p) => p.id === sourceInstanceId
  );
  if (!sourceInstance) return layout;

  // Meme group : reorder
  if (sourceGroup.id === targetGroupId) {
    const oldIdx = sourceGroup.panels.findIndex(
      (p) => p.id === sourceInstanceId
    );
    if (oldIdx < 0) return layout;
    const newRoot = mapTree(layout.root, (n) => {
      if (n.kind !== "tabs" || n.id !== sourceGroup.id) return n;
      const reordered = n.panels.filter((_, i) => i !== oldIdx);
      const insertAt = clampInsertIndex(targetIndex, reordered.length);
      reordered.splice(insertAt, 0, sourceInstance);
      return {
        ...n,
        panels: reordered,
        activeIndex: insertAt,
      };
    });
    if (newRoot === layout.root) return layout;
    return { ...layout, root: newRoot };
  }

  // Autre group : remove from source, insert in target
  const removed = removePanel(layout, sourceInstanceId);
  const newRoot = mapTree(removed.root, (n) => {
    if (n.kind !== "tabs" || n.id !== targetGroupId) return n;
    const insertAt = clampInsertIndex(targetIndex, n.panels.length);
    const newPanels = [...n.panels];
    newPanels.splice(insertAt, 0, sourceInstance);
    return {
      ...n,
      panels: newPanels,
      activeIndex: insertAt,
    };
  });
  return cleanupLayout({ ...removed, root: newRoot });
}

function clampInsertIndex(idx: number, length: number): number {
  if (idx < 0) return 0;
  if (idx > length) return length;
  return idx;
}

/* ============================================================ */
/* splitOnEdge — drag d'un tab existant vers un edge              */
/* ============================================================ */

/**
 * Deplace un tab existant vers un nouveau group cree par split d'un edge
 * d'un node cible. Si le source group devient vide, il est aplati par cleanup.
 *
 * Edge cases :
 *   - dragger l'unique tab d'un group sur un edge du meme group → no-op
 *   - le node cible n'existe pas → no-op
 */
export function splitOnEdge(
  layout: Layout,
  sourceInstanceId: string,
  targetNodeId: string,
  edge: Edge,
  opts?: MutationOptions
): Layout {
  const idFactory = resolveIdFactory(opts);
  const sourceGroup = findGroupContaining(layout, sourceInstanceId);
  if (!sourceGroup) return layout;
  const sourceInstance = sourceGroup.panels.find(
    (p) => p.id === sourceInstanceId
  );
  if (!sourceInstance) return layout;

  // No-op : seul tab du group, dragge sur lui-meme
  if (sourceGroup.id === targetNodeId && sourceGroup.panels.length === 1) {
    return layout;
  }

  // Step 1 : retirer du source
  const removed = removePanel(layout, sourceInstanceId);

  // Step 2 : creer un nouveau group a partir d'un edge du target
  const newGroup: TabGroup = {
    kind: "tabs",
    id: idFactory(),
    panels: [sourceInstance],
    activeIndex: 0,
  };
  return createEdgeSplit(removed, targetNodeId, edge, newGroup, idFactory);
}

/**
 * Remplace `targetNodeId` par un Split contenant `[target, newSibling]`
 * (ou `[newSibling, target]` si l'edge est avant). Direction inferee de l'edge.
 *
 * `sizes` (optionnel) = `[targetShare, newSiblingShare]` en %, dans l'ordre
 * logique target-puis-nouveau ; on l'aligne sur l'ordre reel des enfants
 * (flip si edge avant) et on normalise. Si omis → 50/50.
 *
 * Si `targetNodeId` n'existe pas, retourne le layout inchange (no-op silent).
 */
function createEdgeSplit(
  layout: Layout,
  targetNodeId: string,
  edge: Edge,
  newSibling: LayoutNode,
  idFactory: IdFactory,
  sizes?: readonly [number, number]
): Layout {
  const direction: SplitDirection =
    edge === "left" || edge === "right" ? "row" : "column";
  const before = edge === "left" || edge === "top";
  let touched = false;

  const newRoot = mapTree(layout.root, (n) => {
    if (n.id !== targetNodeId) return n;
    touched = true;
    const children = before ? [newSibling, n] : [n, newSibling];
    // sizes fourni = [target, sibling] ; on l'aligne sur l'ordre des enfants
    // (flip si le nouveau panneau est insere AVANT le target).
    const splitSizes = sizes
      ? normalizeSizes(before ? [sizes[1], sizes[0]] : [sizes[0], sizes[1]])
      : equalSizes(2);
    const newSplit: Split = {
      kind: "split",
      id: idFactory(),
      direction,
      children,
      sizes: splitSizes,
    };
    return newSplit;
  });

  if (!touched) return layout;
  return cleanupLayout({ ...layout, root: newRoot });
}

/* ============================================================ */
/* resizeSplit                                                   */
/* ============================================================ */

export function resizeSplit(
  layout: Layout,
  splitId: string,
  sizes: readonly number[]
): Layout {
  let touched = false;
  const newRoot = mapTree(layout.root, (n) => {
    if (n.kind !== "split" || n.id !== splitId) return n;
    if (sizes.length !== n.children.length) return n; // mismatch ignored
    touched = true;
    return { ...n, sizes: normalizeSizes(sizes) };
  });
  if (!touched) return layout;
  return { ...layout, root: newRoot };
}

/* ============================================================ */
/* setActiveTab                                                  */
/* ============================================================ */

export function setActiveTab(
  layout: Layout,
  groupId: string,
  index: number
): Layout {
  let touched = false;
  const newRoot = mapTree(layout.root, (n) => {
    if (n.kind !== "tabs" || n.id !== groupId) return n;
    if (n.panels.length === 0) return n;
    const clamped = Math.max(0, Math.min(index, n.panels.length - 1));
    if (clamped === n.activeIndex) return n;
    touched = true;
    return { ...n, activeIndex: clamped };
  });
  if (!touched) return layout;
  return { ...layout, root: newRoot };
}

/* ============================================================ */
/* setPanelView — change la vue active d'une instance            */
/* ============================================================ */

/**
 * Change quel "affichage" (view) est actif pour une instance de panel.
 * `viewId` = undefined remet la vue par defaut (le shell utilisera
 * `def.defaultViewId` au render).
 */
export function setPanelView(
  layout: Layout,
  instanceId: string,
  viewId: string | undefined
): Layout {
  let touched = false;
  const newRoot = mapTree(layout.root, (n) => {
    if (n.kind !== "tabs") return n;
    const idx = n.panels.findIndex((p) => p.id === instanceId);
    if (idx < 0) return n;
    const panel = n.panels[idx]!;
    if (panel.viewId === viewId) return n;
    touched = true;
    const updated = buildInstance(panel.id, panel.panelId, {
      viewId,
      params: panel.params as Record<string, unknown> | undefined,
      title: panel.title,
    });
    const newPanels = n.panels.map((p, i) => (i === idx ? updated : p));
    return { ...n, panels: newPanels };
  });
  if (!touched) return layout;
  return { ...layout, root: newRoot };
}

/* ============================================================ */
/* setPanelParams — update les parametres d'une instance         */
/* ============================================================ */

/**
 * Remplace `params` d'une instance. Pour un patch partiel, le caller doit
 * lire les params actuels et fusionner.
 *
 * `params` = undefined retire les params (instance "vanilla").
 */
export function setPanelParams(
  layout: Layout,
  instanceId: string,
  params: Record<string, unknown> | undefined
): Layout {
  let touched = false;
  const newRoot = mapTree(layout.root, (n) => {
    if (n.kind !== "tabs") return n;
    const idx = n.panels.findIndex((p) => p.id === instanceId);
    if (idx < 0) return n;
    const panel = n.panels[idx]!;
    touched = true;
    const updated = buildInstance(panel.id, panel.panelId, {
      viewId: panel.viewId,
      params,
      title: panel.title,
    });
    const newPanels = n.panels.map((p, i) => (i === idx ? updated : p));
    return { ...n, panels: newPanels };
  });
  if (!touched) return layout;
  return { ...layout, root: newRoot };
}
